import { FC } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { Plus } from "lucide-react";
import { Button } from "src/components/ui/button";
import { AnswerRowItem } from "src/features/observationsAdmin/components";
import { useDeleteObservationAnswer } from "src/features/observationsAdmin/hooks";
import { DEFAULT_ADMIN_OBSERVATION_ANSWER } from "src/features/observationsAdmin/constants";

interface Props {
	questionIndex: number;
	isSubmittingQuestion?: boolean;
}

const ObservationAnswersList: FC<Props> = ({
	questionIndex,
	isSubmittingQuestion = false
}) => {
	const { mutate: deleteObservationAnswer } = useDeleteObservationAnswer();

	const { control } = useFormContext<AdminObservationForm>();

	const { append, fields, remove } = useFieldArray({
		control,
		name: `questions.${questionIndex}.answers`
	});

	const { answers } = useWatch({
		control,
		name: `questions.${questionIndex}`
	});

	const handleRemoveAnswerClick = (answerIndex: number) => {
		if (!answers) return;

		const answerId = answers[answerIndex].id;

		if (!answerId) return remove(answerIndex);

		deleteObservationAnswer(answerId, {
			onSuccess: () => remove(answerIndex)
		});
	};

	const handleAddAnswerOptionClick = () => {
		append(
			{
				...DEFAULT_ADMIN_OBSERVATION_ANSWER,
				order: fields.length + 1
			},
			{ shouldFocus: true }
		);
	};

	return (
		<div>
			<div className="flex flex-col gap-6">
				{fields?.map((field, index) => (
					<AnswerRowItem
						key={field.id}
						onRemoveAnswerClick={() => handleRemoveAnswerClick(index)}
						questionIndex={questionIndex}
						answerIndex={index}
						isDisabled={isSubmittingQuestion}
						handleAddAnswerOptionClick={handleAddAnswerOptionClick}
					/>
				))}
			</div>

			<Button
				type="button"
				variant="link"
				className="mt-6"
				onClick={handleAddAnswerOptionClick}
			>
				<Plus />
				Add another answer
			</Button>
		</div>
	);
};

export default ObservationAnswersList;
